<template>
  <section class="platform-hero">
    <div class="container x-large">
      <div class="platform-hero-wrapper">
        <div class="ph-content">
          <div class="headline h2">
            <h2
              ref="headline"
              v-html="data.platform_hero_headline"></h2>
          </div>
          <div
            class="text editor-formatting font-larger"
            v-html="data.platform_hero_text"></div>
        </div>
        <div class="ph-hexes">
          <div ref="lottie"></div>
          <div class="shapes">
            <img
              class="hex"
              src="@/assets/img/hex-outline-blur-large.svg"
              alt=""
              role="presentation" />
            <img
              class="square"
              src="@/assets/img/square-blur.svg"
              alt=""
              role="presentation" />
            <img
              class="circle"
              src="@/assets/img/circle-blur.svg"
              alt=""
              role="presentation" />
          </div>
        </div>
      </div>
      <div class="arrow"></div>
    </div>
  </section>
</template>

<script setup>
/* #META
   {
    "NAME": "PlatformHeroComponent",
    "CLASSNAME": "platform-hero",
    "URL": "http://insitro.local/test/"
   }
  #ENDMETA */
import LottieWeb from 'lottie-web'
import platformHeroJson from '@/assets/lottie/platform-hero.json'
import gsap from 'gsap'
import { ref, onMounted, onBeforeUnmount } from 'vue'
import UseIntroShown from '@/components/composables/useIntroShown.js'
import useModulesReady from '@/components/composables/useModulesReady'

const { modulesLoading } = useModulesReady()
defineProps(['data'])

const lottie = ref(null)
const headline = ref(null)

const { introShown } = UseIntroShown()

const tl = gsap.timeline({
  paused: true,
  delay: 0.5,
})

const hasIntroShown = () => {
  if (!modulesLoading.value && introShown.value) {
    // tl.play()
    LottieWeb.loadAnimation({
      container: lottie.value,
      renderer: 'svg',
      autoplay: true,
      loop: false,
      animationData: platformHeroJson,
    }).addEventListener('DOMLoaded', () => {
      tl.play()
    })
  } else {
    setTimeout(() => {
      hasIntroShown()
    }, 100)
  }
}

const animation = () => {
  tl.staggerTo(
    headline.value.children,
    0.75,
    {
      clipPath: 'polygon(0 0, 100% 0, 100% 110%, 0 110%)',
      y: 0,
    },
    0.15
  )
}

onMounted(() => {
  animation()
  hasIntroShown()
})

onBeforeUnmount(() => {
  LottieWeb.destroy
  tl.kill()
})
</script>

<style lang="scss">
.platform-hero {
  padding: 140px 0 100px;

  @include tablet-landscape {
    padding: 100px 0 80px;
  }

  @include mobile {
    padding: 80px 0 60px;
  }

  .platform-hero-wrapper {
    display: flex;
    justify-content: space-between;

    @include tablet-landscape(1) {
      flex-direction: column;
    }

    .ph-content {
      width: 35%;
      max-width: 500px;
      margin-top: 15%;
      margin-right: 40px;

      @include tablet-landscape {
        margin-right: 30px;
      }

      @include tablet-landscape(1) {
        order: 1;
        width: 100%;
        margin: 0 0 30px;
      }

      .headline {
        h2 {
          line-height: 1.1;

          > * {
            clip-path: polygon(0 0, 100% 0, 100% 0%, 0 0%);
            transform: translateY(100%);
          }
        }
      }

      .text {
        margin: 30px 0 0;
      }
    }

    .ph-hexes {
      position: relative;
      width: 60%;

      @include tablet-landscape(1) {
        order: 0;
        width: 100%;
      }

      .shapes {
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;

        img {
          position: absolute;

          &.hex {
            top: 0;
            right: 10%;
            max-width: 18%;
          }

          &.square {
            top: 8%;
            left: 38%;
            max-width: 9%;
          }

          &.circle {
            bottom: 19%;
            right: 21%;
            max-width: 13%;
          }
        }
      }
    }
  }

  .arrow {
    position: absolute;
    bottom: 0;
    left: 50%;
    width: 36px;
    height: 36px;
    border-radius: 50%;
    transform: translate(-50%, 50%) rotate(125deg);
    background-color: $yellow;

    &:after {
      content: '';
      display: block;
      position: absolute;
      top: 50%;
      left: 50%;
      width: 14px;
      height: 11px;
      transform: translate(-50%, -50%);
      background-image: url('@/assets/img/arrow-out.svg');
      background-size: contain;
      background-repeat: no-repeat;
    }
  }
}
</style>
